import React, { useState, useRef, useEffect } from 'react';
import './CodeInput.scss';
import { appKeyCode } from '@utils/globalConstant';
import PropTypes from 'prop-types';

export function CodeInput({
  id,
  label = null,
  invalid = false,
  valid = false,
  customClass = '',
  onChange = () => {},
  onBlur = () => {},
  onKeyChange = () => {}
}) {
  const inputRef = useRef([]);

  // States
  const [inputValue0, setInputValue0] = useState('');
  const [inputValue1, setInputValue1] = useState('');
  const [inputValue2, setInputValue2] = useState('');
  const [inputValue3, setInputValue3] = useState('');
  const [isFocus, setIsFocus] = useState(false);
  const [isBlur, setIsBlur] = useState(false);
  const [isTouched, setIsTouched] = useState(false);

  // Functions
  const triggerKeyDown = (event, currentIndex) => {
    const {
      keyCode,
      key,
      target: { value }
    } = event;

    // Prevent entering "e", "+", and "-"
    if (key === 'e' || key === 'E' || key === '+' || key === '-') {
      event.preventDefault();
      return;
    }

    if (keyCode === appKeyCode.BACKSPACE && !value && currentIndex > 0) {
      inputRef.current[currentIndex - 1]?.focus();
    }
  };

  const triggerChange = (event, currentIndex) => {
    const { value } = event.target;

    if (value.length > 1) {
      // Distribute the pasted value across the inputs
      const values = value.split('');
      values.forEach((char, index) => {
        if (currentIndex + index < 4) {
          // Ensure values don't overflow beyond last input
          switch (currentIndex + index) {
            case 0:
              setInputValue0(char);
              break;
            case 1:
              setInputValue1(char);
              break;
            case 2:
              setInputValue2(char);
              break;
            case 3:
              setInputValue3(char);
              break;
            default:
              break;
          }
          inputRef.current[currentIndex + index]?.focus();
        }
      });

      setIsTouched(true);
      return;
    }

    // Handle single character input
    const validateInput = value.length > 1 ? value.charAt(value.length - 1) : value;

    switch (currentIndex) {
      case 0:
        setInputValue0(validateInput);
        break;
      case 1:
        setInputValue1(validateInput);
        break;
      case 2:
        setInputValue2(validateInput);
        break;
      case 3:
        setInputValue3(validateInput);
        break;
      default:
        break;
    }

    setIsTouched(true);

    if (validateInput && currentIndex < 3) {
      inputRef.current[currentIndex + 1]?.focus();
    }

    if (onChange) {
      onChange(event);
    }
  };

  const triggerBlur = (event) => {
    if (onBlur) {
      onBlur(event);
    }
    setIsBlur(true);
    setIsFocus(false);
    setIsTouched(false);
  };

  // Hooks
  useEffect(() => {
    if (onKeyChange) {
      onKeyChange(`${inputValue0}${inputValue1}${inputValue2}${inputValue3}`);
    }
  }, [inputValue0, inputValue1, inputValue2, inputValue3]);

  return (
    <div className="mb-3">
      {label && (
        <label htmlFor={id} className="form-label ps-4" id={`${id}-label`}>
          {label}
        </label>
      )}
      <div className="d-flex align-item-center gap-8">
        <div
          className={`input-group-code bg-ek-glass border border-primary rounded
          ${valid ? 'valid' : ''}
          ${invalid ? 'invalid' : ''}
          ${isFocus ? 'focus' : ''}
          ${isBlur ? 'blur' : ''}
          ${isTouched ? 'touched' : ''}
          ${customClass}
      `}>
          <input
            ref={(el) => (inputRef.current[0] = el)}
            className="form-control ek-h1"
            value={inputValue0}
            id={id}
            onFocus={() => {
              setIsFocus(true);
            }}
            onBlur={triggerBlur}
            onChange={(event) => triggerChange(event, 0)}
            onKeyDown={(event) => triggerKeyDown(event, 0)}
            aria-labelledby={`${id}-label`}
            aria-describedby={`${id}-hint ${id}-error ${id}-success`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            min={0}
            max={9}
            maxLength={1}
          />
        </div>
        <div
          className={`input-group-code bg-ek-glass border border-primary rounded
          ${valid ? 'valid' : ''}
          ${invalid ? 'invalid' : ''}
          ${isFocus ? 'focus' : ''}
          ${isBlur ? 'blur' : ''}
          ${isTouched ? 'touched' : ''}
          ${customClass}
      `}>
          <input
            ref={(el) => (inputRef.current[1] = el)}
            className="form-control ek-h1"
            value={inputValue1}
            id={id}
            onFocus={() => {
              setIsFocus(true);
            }}
            onBlur={triggerBlur}
            onChange={(event) => triggerChange(event, 1)}
            onKeyDown={(event) => triggerKeyDown(event, 1)}
            aria-labelledby={`${id}-label`}
            aria-describedby={`${id}-hint ${id}-error ${id}-success`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            min={0}
            max={9}
            maxLength={1}
          />
        </div>
        <div
          className={`input-group-code bg-ek-glass border border-primary rounded
          ${valid ? 'valid' : ''}
          ${invalid ? 'invalid' : ''}
          ${isFocus ? 'focus' : ''}
          ${isBlur ? 'blur' : ''}
          ${isTouched ? 'touched' : ''}
          ${customClass}
      `}>
          <input
            ref={(el) => (inputRef.current[2] = el)}
            className="form-control ek-h1"
            value={inputValue2}
            id={id}
            onFocus={() => {
              setIsFocus(true);
            }}
            onBlur={triggerBlur}
            onChange={(event) => triggerChange(event, 2)}
            onKeyDown={(event) => triggerKeyDown(event, 2)}
            aria-labelledby={`${id}-label`}
            aria-describedby={`${id}-hint ${id}-error ${id}-success`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            min={0}
            max={9}
            maxLength={1}
          />
        </div>
        <div
          className={`input-group-code bg-ek-glass border border-primary rounded
          ${valid ? 'valid' : ''}
          ${invalid ? 'invalid' : ''}
          ${isFocus ? 'focus' : ''}
          ${isBlur ? 'blur' : ''}
          ${isTouched ? 'touched' : ''}
          ${customClass}
      `}>
          <input
            ref={(el) => (inputRef.current[3] = el)}
            className="form-control ek-h1"
            value={inputValue3}
            id={id}
            onFocus={() => {
              setIsFocus(true);
            }}
            onBlur={triggerBlur}
            onChange={(event) => triggerChange(event, 3)}
            onKeyDown={(event) => triggerKeyDown(event, 3)}
            aria-labelledby={`${id}-label`}
            aria-describedby={`${id}-hint ${id}-error ${id}-success`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            min={0}
            max={9}
            maxLength={1}
          />
        </div>
      </div>
    </div>
  );
}

CodeInput.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  invalid: PropTypes.bool,
  valid: PropTypes.bool,
  customClass: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyChange: PropTypes.func
};

export default CodeInput;
